import {
  Component,
  Output,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common'; // Asegúrate de importar CommonModule
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AlertService } from '../../../core/services/alert/alert.service';
import { OrdersService } from '../../../core/services/orders/orders.service';
import { LocalStorageService } from '../../../core/services/localStorage/local-storage.service';
import { AuthStateService } from '../../../core/services/auth-state/auth-state-service.service';
interface Item {
  date: string;
  created_by: string;
  message: string;
  created_by_name: string;
  content: string;
}

@Component({
  selector: 'app-form-add-notes',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    TranslateModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './form-add-notes.component.html',
  styleUrl: './form-add-notes.component.css',
})
export class FormAddNotesComponent implements OnChanges, OnDestroy {
  @Output() backButtonClickDocument = new EventEmitter<boolean>();
  @Output() updateStage = new EventEmitter<boolean>();
  @Output() formSubmit = new EventEmitter<any>();
  @Input() order_reference?: any;
  @Input() require_action_note?: any;
  @Input() require_private_action_note?: any;

  @Output() formSubmitStage = new EventEmitter<any>();
  @Input() activeForm?: any;
  @Input() rules: Record<string, string | null> = {};
  form: FormGroup;
  notes: any[] = [];
  userName: string = '';

  @Output() tabChanged = new EventEmitter<string>();
  showPublicTab: boolean = true;
  activeTab: 'public' | 'internal' = 'public';
  filteredNotes: any[] = [];

  isBrokerSales: boolean = false;
  canCreatePublicNotes: boolean = true;

  countdownMap: Map<string, number> = new Map();
  private countdownInterval: any = null;

  constructor(
    private fb: FormBuilder,
    private alertService: AlertService,
    private orderService: OrdersService,
    private localStorageService: LocalStorageService,
    private authStateService: AuthStateService,
    private translateService: TranslateService
  ) {
    const role = this.authStateService.getRole();
    const accountType = this.authStateService.getAccountType();

    this.isBrokerSales = accountType === 'broker' && role === 'Sales';

    // Default basado en rol (se sobreescribe cuando llegan rules)
    if (role === 'Workshop' || role === 'Sales') {
      this.activeTab = 'internal';
      this.showPublicTab = false;
    } else {
      this.activeTab = 'public';
      this.showPublicTab = true;
    }

    this.form = this.fb.group({
      order_id: [''],
      public: [true],
      content: [''],
    });

    this.updateFormAccess();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['order_reference']) {
      if (this.order_reference) {
        this.form.patchValue({
          order_id: this.order_reference,
        });
      }
    }
    if (changes['rules'] && this.rules) {
      this.applyRulesAccess();
    }
    if (changes['activeForm']) {
      if (this.activeForm === 'notes') {
        this.getNotes();
      }
    }
  }

  private applyRulesAccess(): void {
    // Si el backend ya envía notes_public/notes_private, usarlas
    if ('notes_public' in this.rules || 'notes_private' in this.rules) {
      this.showPublicTab = this.rules['notes_public'] != null;
      this.canCreatePublicNotes = this.rules['notes_public'] === 'w';

      if (!this.showPublicTab && this.activeTab === 'public') {
        this.activeTab = 'internal';
      }
    }
    this.updateFormAccess();
  }

  setActiveTab(tab: 'public' | 'internal') {
    this.activeTab = tab;
    this.tabChanged.emit(tab);
    this.updateFormAccess();

    this.getNotes();
  }

  private updateFormAccess(): void {
    const contentControl = this.form.get('content');
    if (!contentControl) return;

    // Si el backend provee notes_public/notes_private, usar rules
    if ('notes_public' in this.rules || 'notes_private' in this.rules) {
      const ruleKey = this.activeTab === 'public' ? 'notes_public' : 'notes_private';
      if (this.rules[ruleKey] === 'w') {
        contentControl.enable();
      } else {
        contentControl.disable();
        contentControl.setValue('');
      }
    } else {
      // Fallback: lógica legacy por rol
      if (this.activeTab === 'public' && this.isBrokerSales) {
        contentControl.disable();
        contentControl.setValue('');
      } else {
        contentControl.enable();
      }
    }
  }

  emitBackButtonClick() {
    this.order_reference = null;
    this.backButtonClickDocument.emit(true);
  }

  emitUpdateStage() {
    this.updateStage.emit(true);
  }

  emitChangeStage(stage: string) {
    this.formSubmitStage.emit(stage);
  }

  async onSubmit(flagSuggest: boolean) {
    // Bloquear si no tiene permiso de escritura en el tab activo
    const hasRulesSplit = 'notes_public' in this.rules || 'notes_private' in this.rules;
    if (hasRulesSplit) {
      const ruleKey = this.activeTab === 'public' ? 'notes_public' : 'notes_private';
      if (this.rules[ruleKey] !== 'w') {
        this.alertService.warning('swal_messages.notes.no_public_notes_permission');
        return;
      }
    } else if (this.activeTab === 'public' && this.isBrokerSales) {
      this.alertService.warning(
        'swal_messages.notes.no_public_notes_permission'
      );
      return;
    }

    if (this.form.valid) {
      let noteData = {
        ...this.form.value,
        visibility: this.activeTab === 'public' ? 'public' : 'private',
      };
      if (flagSuggest) {
        noteData = {
          ...noteData,
          suggest: flagSuggest,
        };
      }
      if (this.activeTab === 'internal') {
        noteData = {
          ...noteData,
          require_action: await this.askPrivate()
        };
      }

      this.orderService.addNotes(noteData).subscribe({
        next: (data) => {
          if (data.data.next_stage != null) {
            // Obtener las traducciones necesarias
            const stageTranslation = this.translateService.instant(`badge-status_select-stage.${data.data.next_stage}`);
            const messageWithParams = this.translateService.instant('swal_messages.change_stage_message', { stage: stageTranslation });
            const title = this.translateService.instant('swal_messages.change_stage');
            const confirmButtonText = this.translateService.instant('swal_messages.confirm');
            const cancelButtonText = this.translateService.instant('swal_messages.notes.cancel');

            this.alertService
              .confirmation2(
                messageWithParams,
                title,
                confirmButtonText,
                cancelButtonText
              )
              .then(async (result) => {
                if (result.isConfirmed) {
                  this.emitChangeStage(data.data.next_stage);
                } else {
                  this.onSubmit(false);
                }
              });
          } else {
            this.alertService
              .successOk('swal_messages.add_notes_success')
              .then(async (result) => {
                if (result.isConfirmed) {
                  this.getNotes();
                  this.form.patchValue({
                    content: '',
                  });
                  this.emitUpdateStage();
                }
              });
          }
        },
        error: (error) => {
          this.alertService.error(error.error).then((result) => {
            if (result.isConfirmed) {
            }
          });
        },
      });
    } else {
      this.alertService.warning('swal_messages.please_complete_form');
      this.alertService.close();
    }
  }



  async askPrivate(): Promise<boolean> {
    const actionResult = await this.alertService.twoOptions(
      'swal_messages.notes.action_required_message',
      'swal_messages.notes.action_required_title',
      'swal_messages.notes.action_required_true',
      'swal_messages.notes.action_required_false'
    );
    if (actionResult.isConfirmed) {
      return true;
    } else {
      return false;
    }
  }

  ngOnDestroy(): void {
    this.stopCountdowns();
  }

  getNotes() {
    this.userName = this.authStateService.getName() || '';
    this.alertService.loading('swal_messages.please_wait');

    const visibility = this.activeTab === 'public' ? 'public' : 'private';

    this.orderService.getNotes(this.order_reference, visibility).subscribe({
      next: (data: any) => {
        this.notes = data.data;
        this.notes.sort((a, b) => {
          return this.timeToNumber(a.date) - this.timeToNumber(b.date);
        });
        this.startCountdowns();
        this.alertService.close();
      },
      error: (error: any) => { },
    });
  }

  startCountdowns(): void {
    this.stopCountdowns();
    this.countdownMap.clear();

    for (const group of this.notes) {
      for (const note of group.notes) {
        if (note.is_deletable && note.deletable_until) {
          const deadline = new Date(note.deletable_until).getTime();
          const remaining = Math.max(0, Math.floor((deadline - Date.now()) / 1000));
          if (remaining > 0) {
            this.countdownMap.set(note.reference, remaining);
          } else {
            note.is_deletable = false;
          }
        }
      }
    }

    if (this.countdownMap.size > 0) {
      this.countdownInterval = setInterval(() => {
        let hasActive = false;
        for (const [ref, seconds] of this.countdownMap) {
          const newVal = seconds - 1;
          if (newVal <= 0) {
            this.countdownMap.delete(ref);
            for (const group of this.notes) {
              for (const note of group.notes) {
                if (note.reference === ref) {
                  note.is_deletable = false;
                }
              }
            }
          } else {
            this.countdownMap.set(ref, newVal);
            hasActive = true;
          }
        }
        if (!hasActive) {
          this.stopCountdowns();
        }
      }, 1000);
    }
  }

  stopCountdowns(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  formatCountdown(reference: string): string {
    const seconds = this.countdownMap.get(reference) || 0;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  async deleteNote(reference: string): Promise<void> {
    const result = await this.alertService.confirmation(
      'swal_messages.order_notes.note_delete_confirm_message',
      'swal_messages.order_notes.note_delete_confirm_title',
      'swal_messages.confirm',
      'swal_messages.cancel'
    );

    if (!result.isConfirmed) return;

    this.alertService.loading('swal_messages.please_wait');

    this.orderService.deleteNote(reference).subscribe({
      next: () => {
        this.alertService.close();
        this.alertService
          .successOk('swal_messages.order_notes.order_note_deleted')
          .then(() => {
            this.getNotes();
            this.emitUpdateStage();
          });
      },
      error: (err: any) => {
        this.alertService.close();
        const errors = err.error?.errors || err.error;
        this.alertService.error(errors);
      },
    });
  }

  getClass(item: string): string {
    const ownerMap: { [key: string]: string } = {
      me: 'reverse-me',
      team: 'reverse',
      other: '',
    };

    return ownerMap[item] || '';
  }

  timeToNumber(time: any) {
    return parseInt(time.replace(/:/g, ''));
  }

  getFormattedText(text: string): string {
    return String(text).replace(/\n/g, '<br>');
  }

  updateSelectedNotes(actionRequired: boolean): void {
    const selectedIds = [this.order_reference];
    if (selectedIds.length === 0) {
      return;
    }

    this.orderService
      .updateActionRequiredBatch(selectedIds, actionRequired)
      .subscribe({
        next: () => {
          this.alertService.success('swal_messages.notes.updated_succesfully');
          this.emitUpdateStage();
        },
        error: (error) => {
          this.alertService.error('Error al actualizar las notas.');
        },
      });
  }

  updatePrivateSelectedNotes(actionRequired: boolean): void {
    const selectedIds = [this.order_reference];
    if (selectedIds.length === 0) {
      return;
    }
    this.orderService
      .updatePrivateActionRequiredBatch(selectedIds, actionRequired)
      .subscribe({
        next: () => {
          this.alertService.success('swal_messages.notes.updated_succesfully');
          this.emitUpdateStage();
        },
        error: (error) => {
          this.alertService.error('Error al actualizar las notas.');
        },
      });
  }
}
